import React, { useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { titleCase, upperCase, formatDateTime, formatDateOnly } from '../../../lib/utils';
import { TrackingDataPTL } from '../../../types/tracking';
import OrderDetails from './OrderDetails';
import { generateShareLink } from 'features/shipments/api';
import { Button } from 'components/ui/button';
import ShareLinkModal from 'components/ui/share-link-modal';

interface TrackingPagePTLProps {
    data: TrackingDataPTL;
    isPublic?: boolean;
}

const MilestoneStepper: React.FC<{ milestones: TrackingDataPTL['milestones'] }> = ({ milestones }) => {
    return (
        <div className="w-full py-8">
            <div className="flex items-start justify-between px-4">
                {milestones.map((milestone, index) => {
                    const isCompleted = milestone.completed;
                    const isCurrent = milestone.current;
                    const isLast = index === milestones.length - 1;

                    // Determine if the line to the next milestone should be green
                    const nextMilestone = milestones[index + 1];
                    const lineToNextGreen = nextMilestone && (nextMilestone.completed || nextMilestone.current);

                    return (
                        <React.Fragment key={milestone.id}>
                            {/* Milestone Node */}
                            <div className="flex flex-col items-center">
                                {/* Icon/Dot - Both truck and circle are in 48x48px containers */}
                                <div className="w-12 h-12 flex items-center justify-center z-10">
                                    {isCurrent ? (
                                        <img
                                            src="/images/truck.png"
                                            alt="Truck"
                                            className="w-full h-full object-contain"
                                        />
                                    ) : (
                                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isCompleted ? 'bg-green-500' : 'bg-gray-300'}`}>
                                            {isCompleted && (
                                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Label */}
                                <div className={`mt-3 text-sm font-medium whitespace-nowrap ${isCurrent || isCompleted ? 'text-gray-900' : 'text-gray-500'}`}>
                                    {milestone.label}
                                </div>
                            </div>

                            {/* Connecting Line (except after last item) */}
                            {!isLast && (
                                <div className="flex-1 flex items-center px-2" style={{ paddingTop: '24px' }}>
                                    <div className={`h-1 w-full ${lineToNextGreen ? 'bg-green-500' : 'bg-gray-300'}`} />
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

const TrackingPagePTL: React.FC<TrackingPagePTLProps> = ({ data, isPublic }) => {
    const cnNumber = data.cn_number;
    const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
    const [shareUrl, setShareUrl] = useState<string>("");
    const [error, setError] = useState<string | null>(null);

    const lastUpdatedFormatted = formatDateTime(data.last_updated);

    const handleShareClick = async () => {
        if (!cnNumber) return;
        try {
            const response = await generateShareLink(cnNumber);
            const fullUrl = `${window.location.origin}/shipments/track/public?token=${response.share_token}`;
            setShareUrl(fullUrl);
            setIsShareModalOpen(true);
        } catch (err) {
            setError("Failed to generate share link.");
        }
    };

    const handleCopy = async () => {
        await navigator.clipboard.writeText(shareUrl);
        setIsShareModalOpen(false);
    };

    const isOnHold = data.status === 'on_hold';
    const isAwaitingPickup = data.status === 'awaiting_pickup';
    const stockDays = (isOnHold || isAwaitingPickup) && data.stock_since
        ? Math.floor((new Date().getTime() - new Date(data.stock_since).getTime()) / (1000 * 60 * 60 * 24))
        : 0;

    return (
        <div className="h-full w-full overflow-y-auto bg-white p-6">
            {!isPublic && (
                <div className="absolute top-4 right-6 z-30">
                    <Button variant="outline" size="sm" onClick={handleShareClick}>
                        <span className="mr-2">🔗</span> Share Link
                    </Button>
                </div>
            )}
            <div className="max-w-4xl mx-auto space-y-6">
                {error && (
                    <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded">
                        {error}
                    </div>
                )}
                {isPublic && (
                    <div className="mb-6 text-center md:text-left">
                        <img src="/logo.png" className="h-10 mb-4 mx-auto md:mx-0" />
                        <h2 className="text-xl font-bold text-gray-800">Shipment Tracking</h2>
                        <p className="text-sm text-gray-500">
                            CN Number: <span className="font-semibold text-gray-700">{data.cn_number}</span>
                        </p>
                    </div>
                )}

                {/* Header Section: Consignor, Consignee, ETA */}
                <div className="flex flex-col md:flex-row md:justify-between gap-8">
                    <div className="flex-1">
                        <div className="text-gray-500 text-sm mb-1">From</div>
                        <div className="text-lg font-bold text-gray-900 mb-1">{upperCase(data.consignor)}</div>
                        <div className="text-sm text-gray-600">left {titleCase(data.origin)}</div>
                    </div>

                    <div className="flex-1 md:text-right">
                        <div className="text-gray-500 text-sm mb-1">Expected Delivery Date</div>
                        <div className="text-lg font-bold text-gray-900 mb-1">{formatDateTime(data.eta).date}</div>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row md:justify-between gap-8">
                    <div className="flex-1">
                        <div className="text-gray-500 text-sm mb-1">To</div>
                        <div className="text-lg font-bold text-gray-900 mb-1">{upperCase(data.consignee)}</div>
                        <div className="text-sm text-gray-600">arriving at {titleCase(data.destination)}</div>
                    </div>
                </div>

                {/* Milestones */}
                <div className="mt-4">
                    <MilestoneStepper milestones={data.milestones} />
                </div>

                {/* Current Location (Blue Box) */}
                <div className={`mb-4 p-4 rounded-lg border ${(isOnHold || isAwaitingPickup)
                    ? 'bg-orange-50 border-orange-200'
                    : 'bg-blue-50 border-blue-200'
                    }`}>
                    <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full animate-pulse ${(isOnHold || isAwaitingPickup) ? 'bg-orange-500' : 'bg-blue-500'}`}></div>
                        <span className={`text-sm font-medium ${(isOnHold || isAwaitingPickup) ? 'text-orange-900' : 'text-blue-900'
                            }`}>
                            {isAwaitingPickup
                                ? `Awaiting Pickup at ${data.current_address} since ${data.stock_since ? formatDateOnly(data.stock_since) : '-'} (${stockDays} days)`
                                : isOnHold
                                ? `Currently on hold at ${data.current_address} since ${data.stock_since ? formatDateOnly(data.stock_since) : '-'} (${stockDays} days)`
                                : `Currently in ${data.current_address}`
                            }
                        </span>
                    </div>
                    <div className={`text-xs mt-1 ${(isOnHold || isAwaitingPickup) ? 'text-orange-700' : 'text-blue-700'
                        }`}>
                        Last updated: {lastUpdatedFormatted.date} {lastUpdatedFormatted.time}
                    </div>
                </div>

                {/* Shipment Progress */}
                <Card className="bg-white shadow-sm">
                    <CardContent className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-6">Shipment Progress</h3>
                        <div className="space-y-0">
                            {data.progress.map((item, idx) => (
                                <div key={idx} className="flex items-center py-3 border-b border-gray-100 last:border-b-0">
                                    <div className="min-w-[140px] text-sm font-semibold text-gray-900 flex-shrink-0">
                                        {formatDateTime(item.timestamp).date}
                                    </div>
                                    <div className="text-gray-400 mx-4 flex-shrink-0">|</div>
                                    <div className="text-sm text-gray-600 flex-1">
                                        {item.update}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Order Details */}
                <OrderDetails orderDetails={data.order_details} />

                <ShareLinkModal
                    isOpen={isShareModalOpen}
                    shareUrl={shareUrl}
                    onCopy={handleCopy}
                    onClose={() => setIsShareModalOpen(false)}
                />
            </div>
        </div>
    );
};

export default TrackingPagePTL;
